/* eslint-disable react/prop-types, react/destructuring-assignment */
import { isEmpty } from 'lodash'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { PERMISSION_LEVELS } from '../../../../../_augmentt/selectors/userPermissions'
import { ModalPanel, ModalSidebar, TabSwitch } from '../../../../components/ModalSidebar'
import { ToastContext } from '../../../../context/ToastContext'
import { getEmployee } from '../../../../crud/employees.crud'
import { isSecureEnabledForTenant, isSecurePremiumEnabledForTenant } from '../../../../crud/orgs.crud'
import useAllEmployees from '../../../../hooks/useAllEmployees'
import useGroupsFetch from '../../../../hooks/useGroupsFetch'
import { adaptGroupsToStandardFormat } from '../../../../util/groups.util'
import { normalizeEmployeeLicenseType } from '../../../../util/employeeLicenses.util'
import { getEmployeeDirectoryId, getEmployeeFullName } from '../../../../util/table.util'
import { GWS } from '../../engage/utils'
import { resolveDirectoryIntegrationForModal } from '../../utils/integrations'
import EditEmployeeDataModal from '../EditEmployeeDataModal'
import IntegrationModals, { useIntegrationModals } from '../IntegrationModals'
import UserPanelTitle from '../../../../components/UserPanelTitle'
import EmployeeActionsModals from './EmployeeActionsModals'
import { MailboxDelegationsProvider } from './MailboxDelegations/useMailboxDelegations'
import LoadingTab from './LoadingTab/LoadingTab'
import Overview from './Overview'
import SystemUserSection from './SystemUserSection/SystemUserSection'
import UserProperties from './UserProperties'
import useSystemUserDetails from './useSystemUserDetails'
import usePanelTabs, { TAB_KEYS } from './usePanelTabs'

const modalInitialState = {
  selectedEmployee: null,
  openedModalName: null,
}
const NO_TABS = []

/**
 * Details of one person: their Microsoft 365 details and actions when they are an employee, and their
 * Augmentt account settings on the System User tab when they are a system user the viewer can manage.
 */
function ViewEmployeeDataModal(props) {
  const {
    show,
    onHide,
    tableRowEmployee,
    selectedCustomer,
    intl,
    overrideEngageLicensing = false,
    allowRemediation = false,
    allEmployeesGroups = [],
    allEmployees = [],
    onUpdate,
    selectedIntegration: selectedIntegrationFromPage,
    selectedIntegrationType: selectedIntegrationTypeFromPage,
    // Set by System Users, which opens the panel for a system user rather than an employee.
    systemUserId: systemUserIdFromPage,
    initialTabId,
    // Opens the System User tab's edit panel straight away, as Edit on System Users does.
    startInSystemUserEdit = false,
    // System Users already holds the user and the lists the System User tab needs, so it shows them at once.
    systemUserDetails: providedSystemUserDetails,
  } = props

  const [isLoading, setLoading] = useState(true)
  const [employee, setEmployee] = useState({})

  // For integrated users the identifier is employeeId; for non-integrated users it is id.
  const tableRowIdentifier = tableRowEmployee?.employeeId || tableRowEmployee?.id
  // Loading is keyed on that identifier, not on the row object: callers rebuild an equal row object
  // on their own renders, and refetching for the employee already displayed would flash the panel.
  // The ref carries the newest row data into the fetch without retriggering it.
  const tableRowEmployeeRef = useRef(tableRowEmployee)
  // Callers that resolve the identifier asynchronously pass the row twice: pending first, resolved
  // after. That transition has to reach the fetch even when the identifier itself does not change.
  const isPendingEmployeeIdResolution = !!tableRowEmployee?.pendingEmployeeIdResolution
  // A system user without a Microsoft 365 link opens the panel without an employee.
  const hasEmployee = !!tableRowIdentifier

  const [showEditModal, setShowEditModal] = useState(false)

  // License assignment state
  const [discoverLicensesEnable, setDiscoverLicensesEnable] = useState(false)
  const [engageLicensesEnable, setEngageLicensesEnable] = useState(false)
  const [secureLicensesEnable, setSecureLicensesEnable] = useState(false)

  const [isSecureEnabled, setIsSecureEnabled] = useState(false)
  const [isSecurePremiumEnabled, setIsSecurePremiumEnabled] = useState(false)

  const [numDiscoverAvailable, setNumDiscoverAvailable] = useState(0)
  const [numEngageAvailable, setNumEngageAvailable] = useState(0)
  const [numSecureAvailable, setNumSecureAvailable] = useState(0)

  // IntegrationModals hook (for the 4 basic actions)
  const { closeIntegrationModal, modalData, openedModal, openIntegrationModal } = useIntegrationModals()

  // Modal state management (similar to Engage.js pattern) for IntegrationManager and other modals
  const [modal, setEngageModal] = useState(modalInitialState)
  const [action, setAction] = useState(null)

  // Global state
  const currentUser = useSelector(({ user }) => user.data)
  const orgLicenses = useSelector(({ licenses }) => licenses.data)
  const permissions = useSelector(({ userPermissions }) => userPermissions?.data?.permissions ?? {})

  // User modification actions require Engage Manage (on Secure pages and everywhere).
  const canPerformActions = useMemo(() => permissions.engage === PERMISSION_LEVELS.MANAGE, [permissions.engage])
  const reduxSelectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)
  const reduxIntegrations = useSelector(({ integrations }) => integrations.integrations)
  const directoryIntegration = useMemo(
    () =>
      resolveDirectoryIntegrationForModal({
        selectedIntegration: selectedIntegrationFromPage,
        selectedIntegrationType: selectedIntegrationTypeFromPage,
        reduxSelectedIntegration,
        integrations: reduxIntegrations,
        customerId: selectedCustomer?.id,
      }),
    [
      selectedIntegrationFromPage,
      selectedIntegrationTypeFromPage,
      reduxSelectedIntegration,
      reduxIntegrations,
      selectedCustomer?.id,
    ],
  )
  const integrationApplicationName = directoryIntegration?.IntegrationApplication?.application_name
  const isGws = integrationApplicationName === GWS

  // Fetch groups if not provided via props
  const { fetchGroups, groups } = useGroupsFetch()

  // Use centralized hook for employee fetching
  const { allEmployees: allEmployeesFromHook } = useAllEmployees({
    allEmployees,
    selectedCustomer,
    show: show && hasEmployee,
  })

  // The System User tab is for viewers who manage system users, on a person who is one.
  const canManageSystemUsers = permissions.systemUsers === PERMISSION_LEVELS.MANAGE
  const systemUserDetails = useSystemUserDetails({
    systemUserId: systemUserIdFromPage ?? employee.systemUserId,
    providedDetails: providedSystemUserDetails,
    // System Users shows its own users read-only to viewers who cannot manage them.
    enabled: show && (canManageSystemUsers || !!providedSystemUserDetails),
  })
  const { systemUser, refresh: refreshSystemUser } = systemUserDetails
  // Kept here rather than in the tab, which unmounts when another tab is chosen.
  const [isEditingSystemUser, setEditingSystemUser] = useState(false)
  // Opened after this panel's modal registers, so the edit panel stacks on top and keeps focus and Escape.
  useEffect(() => {
    if (startInSystemUserEdit) setEditingSystemUser(true)
  }, [startInSystemUserEdit])
  const showSystemUserTab = !!systemUser
  // A viewer with Engage disabled only gets the Microsoft 365 tabs when there is no System User tab instead.
  const showEmployeeTabs = hasEmployee && !(showSystemUserTab && permissions.engage === PERMISSION_LEVELS.DISABLED)

  // Use prop allEmployeesGroups if available, otherwise use fetched groups
  let allEmployeesGroupsFinal = []
  if (allEmployeesGroups.length > 0) {
    allEmployeesGroupsFinal = allEmployeesGroups
  } else if (groups.length > 0) {
    allEmployeesGroupsFinal = adaptGroupsToStandardFormat(groups)
  }

  // Toast notifications
  const [, setToastInfo] = useContext(ToastContext)

  // Update license enable states when employee data changes
  useEffect(() => {
    if (show && employee?.displayLicenses) {
      setDiscoverLicensesEnable(employee.displayLicenses.discover.assigned)
      setEngageLicensesEnable(employee.displayLicenses.engage.assigned)
      setSecureLicensesEnable(employee.displayLicenses.secure.assigned)
    }
  }, [show, employee])

  // Calculate available licenses from orgLicenses
  useEffect(() => {
    if (orgLicenses && selectedCustomer) {
      let numDiscover = 0
      let numEngage = 0
      let numSecure = 0

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const license in orgLicenses) {
        if (!selectedCustomer.parent_id) {
          if (license === 'nfr-discover-essentials') {
            numDiscover += orgLicenses[license].remaining
          }
        }
        if (license === 'discover-essentials' || license === 'discover-premium') {
          numDiscover += orgLicenses[license].remaining
        }
        if (!selectedCustomer.parent_id) {
          if (license === 'nfr-engage-essentials') {
            numEngage += orgLicenses[license].remaining
          }
        }
        if (license === 'engage-essentials' || license === 'engage-premium') {
          numEngage += orgLicenses[license].remaining
        }

        if (license === 'secure-essentials') {
          numSecure += orgLicenses[license].remaining
        }
      }

      setNumDiscoverAvailable(numDiscover)
      setNumEngageAvailable(numEngage)
      setNumSecureAvailable(numSecure)
    }
  }, [orgLicenses, selectedCustomer]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    tableRowEmployeeRef.current = tableRowEmployee
  }, [tableRowEmployee])

  useEffect(() => {
    const tableRow = tableRowEmployeeRef.current

    // Normalize license arrays so null/undefined entries never cause issues
    const safeM365 = Array.isArray(tableRow?.m365Licenses) ? tableRow.m365Licenses.filter((l) => l != null) : undefined
    const safeLicenseType = Array.isArray(tableRow?.licenseType)
      ? tableRow.licenseType.filter((l) => l != null)
      : undefined

    // Seed from the table row only when the panel switches to another employee. Re-running for the
    // employee already displayed must keep the loaded data instead of clearing it.
    setEmployee((currentEmployee) => {
      const currentIdentifier = currentEmployee?.employeeId || currentEmployee?.id
      if (currentIdentifier && currentIdentifier === tableRowIdentifier) {
        return currentEmployee
      }
      return {
        ...tableRow,
        ...(safeM365 !== undefined && { m365Licenses: safeM365 }),
        ...(safeLicenseType !== undefined && { licenseType: safeLicenseType }),
      }
    })

    const fetchEmployee = async () => {
      if (isPendingEmployeeIdResolution) {
        setLoading(false)
        return
      }

      setLoading(true)

      if (isEmpty(selectedCustomer) || isEmpty(tableRow) || !tableRowIdentifier) {
        setLoading(false)
        return
      }

      // load IsSecureEnabled
      const includeNFR = tableRow.licenses?.includes('nfr-secure-essentials')
      const responses = await Promise.all([
        isSecureEnabledForTenant(selectedCustomer.id, false),
        isSecurePremiumEnabledForTenant(selectedCustomer.id, includeNFR),
        getEmployee(tableRowIdentifier),
      ])
      setIsSecureEnabled(responses[0])
      setIsSecurePremiumEnabled(responses[1])

      // Get the latest employee data
      let latestEmployee = responses[2]?.data

      // Filter out null entries from m365Licenses if present
      if (latestEmployee?.m365Licenses && Array.isArray(latestEmployee.m365Licenses)) {
        latestEmployee.m365Licenses = latestEmployee.m365Licenses.filter((license) => license != null)
      }

      latestEmployee = normalizeEmployeeLicenseType(latestEmployee, integrationApplicationName)

      const combinedEmployee = {
        ...tableRow,
        ...latestEmployee,
        isSecureEnabled: responses[0],
        isSecurePremiumEnabled: responses[1],
      }
      setEmployee(combinedEmployee)
      setLoading(false)
    }
    fetchEmployee()
  }, [selectedCustomer, tableRowIdentifier, isPendingEmployeeIdResolution, integrationApplicationName])

  // Check if employee is integrated
  const isIntegrated = useMemo(
    () => !!(employee?.employeeIdentifier || tableRowEmployee?.employeeIdentifier),
    [employee?.employeeIdentifier, tableRowEmployee?.employeeIdentifier],
  )

  // Fetch groups if not provided via props and user is integrated
  useEffect(() => {
    if (show && selectedCustomer?.id && allEmployeesGroups.length === 0 && isIntegrated && !isGws) {
      fetchGroups({ customerId: selectedCustomer.id })
    }
  }, [show, selectedCustomer?.id, allEmployeesGroups.length, isIntegrated, isGws, fetchGroups])

  const modalTabs = usePanelTabs({ showEmployeeTabs, showSystemUserTab })
  // A ready System User tab shows while the employee loads; only the Microsoft 365 tabs wait for it.
  const isPanelLoading = systemUserDetails.isLoading || (isLoading && !showSystemUserTab)
  const isEmployeeTabLoading = isLoading && !isPanelLoading
  // Tabs wait for the first load, so the panel can open on `initialTabId` once that tab exists.
  const [isFirstLoadDone, setFirstLoadDone] = useState(false)
  useEffect(() => {
    if (!isPanelLoading) setFirstLoadDone(true)
  }, [isPanelLoading])
  const panelTabs = isFirstLoadDone ? modalTabs : NO_TABS

  // Until the employee loads, the system user's own name and email stand in.
  const systemUserName = `${systemUser?.first_name ?? ''} ${systemUser?.last_name ?? ''}`.trim()
  const titleName = (hasEmployee && getEmployeeFullName(employee)) || systemUserName
  const titleEmail = (hasEmployee && employee.email) || systemUser?.email
  const directoryUserId = hasEmployee && !isGws ? getEmployeeDirectoryId(employee) : undefined
  const isSignInBlocked = hasEmployee && (employee.isUserSigninEnabled === false || employee.enabled === false)
  const customTitle = useMemo(
    () => (
      <UserPanelTitle
        name={titleName}
        email={titleEmail}
        customerId={selectedCustomer?.id}
        directoryUserId={directoryUserId}
        isSignInBlocked={isSignInBlocked}
      >
        {employee.ignored && <span className="badge badge-secondary">Ignored</span>}
      </UserPanelTitle>
    ),
    [titleName, titleEmail, selectedCustomer?.id, directoryUserId, isSignInBlocked, employee.ignored],
  )

  // Handle edit button click
  const handleEdit = () => {
    setShowEditModal(true)
  }

  // Handle edit modal close
  const handleEditModalClose = () => {
    setShowEditModal(false)
  }

  const refreshEmployeeData = React.useCallback(
    (showSuccessToast = false) => {
      const tableRow = tableRowEmployeeRef.current
      if (selectedCustomer && tableRowIdentifier) {
        setLoading(true)
        // Load IsSecureEnabled along with employee data
        const includeNFR = tableRow.licenses?.includes('nfr-secure-essentials')
        Promise.all([
          isSecureEnabledForTenant(selectedCustomer.id, false),
          isSecurePremiumEnabledForTenant(selectedCustomer.id, includeNFR),
          getEmployee(tableRowIdentifier),
        ])
          .then((responses) => {
            let latestEmployee = responses[2]?.data

            // Transform groupsByType to expected frontend format
            if (latestEmployee?.groupsByType && !latestEmployee?.m365Groups) {
              latestEmployee.m365Groups = latestEmployee.groupsByType.M365Group || []
              latestEmployee.securityGroups = latestEmployee.groupsByType.SecurityGroup || []
              latestEmployee.distributionGroups = latestEmployee.groupsByType.DistributionGroup || []
              latestEmployee.securityMailEnabledGroups = latestEmployee.groupsByType.SecurityMailEnabledGroup || []
              latestEmployee.sharedMailboxes = latestEmployee.groupsByType.SharedMailbox || []
            }

            latestEmployee = normalizeEmployeeLicenseType(latestEmployee, integrationApplicationName)

            const combinedEmployee = {
              ...tableRow,
              ...latestEmployee,
              isSecureEnabled: responses[0],
              isSecurePremiumEnabled: responses[1],
            }
            setEmployee(combinedEmployee)
            setIsSecureEnabled(responses[0])
            setIsSecurePremiumEnabled(responses[1])
            if (showSuccessToast) {
              setToastInfo({ show: true, type: 'GENERAL.ACTION_SUCCESS' })
            }
          })
          .catch((error) => {
            console.error('Error refreshing employee:', error)
            setToastInfo({
              show: true,
              type: error?.response?.data?.message || error?.message || 'GENERAL.ACTION_FAILED',
            })
          })
          .finally(() => {
            setLoading(false)
          })
      }
    },
    [selectedCustomer, tableRowIdentifier, integrationApplicationName, setToastInfo],
  )

  const notifyListUpdated = React.useCallback(() => {
    onUpdate?.()
  }, [onUpdate])

  const handleSystemUserSaved = React.useCallback(() => {
    refreshSystemUser()
    notifyListUpdated()
  }, [refreshSystemUser, notifyListUpdated])

  const handleEmployeeMutationComplete = React.useCallback(
    (showSuccessToast = false) => {
      refreshEmployeeData(showSuccessToast)
      notifyListUpdated()
    },
    [notifyListUpdated, refreshEmployeeData],
  )

  // Handle edit modal update (refresh employee data and the source list after the hub task)
  const handleEditModalUpdate = () => {
    handleEmployeeMutationComplete(false)
    setShowEditModal(false)
  }

  // Modal management functions (similar to Engage.js)
  const closeModal = React.useCallback(() => {
    setEngageModal(modalInitialState)
    setAction(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setModal = React.useCallback((modalConfig) => {
    if (modalConfig?.selectedEmployee) {
      setEngageModal({
        selectedEmployee: modalConfig.selectedEmployee,
        openedModalName: modalConfig.openedModalName,
      })
    } else {
      setEngageModal({
        selectedEmployee: null,
        openedModalName: modalConfig?.openedModalName,
      })
    }
  }, [])

  const setIntegrationManagerAction = React.useCallback((actionValue) => {
    setAction(actionValue)
  }, [])

  // Track when any modal is open
  const isIntegrationModalOpen = !!modal.openedModalName || !!openedModal
  const mailboxEmployeeId = employee?.id ?? employee?.employeeId ?? null
  const shareMailboxDelegations =
    directoryIntegration?.IntegrationApplication?.application_name !== GWS &&
    employee?.isUserMailBoxAvailable === true &&
    selectedCustomer?.id != null &&
    mailboxEmployeeId != null

  return (
    <MailboxDelegationsProvider
      customerId={selectedCustomer?.id}
      employeeId={mailboxEmployeeId}
      enabled={shareMailboxDelegations}
    >
      <ModalSidebar
        show={show}
        onHide={onHide}
        size="lg"
        title={customTitle}
        isLoading={isPanelLoading}
        toBackdrop={(isIntegrationModalOpen || showEditModal) && show}
      >
        <ModalPanel leftButton="close" tabs={panelTabs} initialTabId={initialTabId}>
          {showEmployeeTabs && (
            <>
              <TabSwitch tab={TAB_KEYS.OVERVIEW}>
                {isEmployeeTabLoading ? (
                  <LoadingTab />
                ) : (
                  <Overview
                    employee={employee}
                    selectedCustomer={selectedCustomer}
                    onEdit={handleEdit}
                    allowRemediation={allowRemediation}
                    intl={intl}
                    currentUser={currentUser}
                    orgLicenses={orgLicenses}
                    overrideEngageLicensing={overrideEngageLicensing}
                    canPerformActions={canPerformActions}
                    isLoading={isLoading}
                    setLoading={setLoading}
                    discoverLicensesEnable={discoverLicensesEnable}
                    setDiscoverLicensesEnable={setDiscoverLicensesEnable}
                    engageLicensesEnable={engageLicensesEnable}
                    setEngageLicensesEnable={setEngageLicensesEnable}
                    secureLicensesEnable={secureLicensesEnable}
                    setSecureLicensesEnable={setSecureLicensesEnable}
                    isSecureEnabled={isSecureEnabled}
                    isSecurePremiumEnabled={isSecurePremiumEnabled}
                    numDiscoverAvailable={numDiscoverAvailable}
                    numEngageAvailable={numEngageAvailable}
                    numSecureAvailable={numSecureAvailable}
                    setAction={setIntegrationManagerAction}
                    setModal={setModal}
                    openIntegrationModal={openIntegrationModal}
                    selectedIntegration={directoryIntegration}
                  />
                )}
              </TabSwitch>

              <TabSwitch tab={TAB_KEYS.USER_PROPERTIES}>
                {isEmployeeTabLoading ? (
                  <LoadingTab />
                ) : (
                  <UserProperties
                    employee={employee}
                    intl={intl}
                    selectedCustomer={selectedCustomer}
                    onEdit={handleEdit}
                    overrideEngageLicensing={overrideEngageLicensing}
                    canPerformActions={canPerformActions}
                    setAction={setIntegrationManagerAction}
                    setModal={setModal}
                    openIntegrationModal={openIntegrationModal}
                    selectedIntegration={directoryIntegration}
                  />
                )}
              </TabSwitch>
            </>
          )}

          {showSystemUserTab && (
            <TabSwitch tab={TAB_KEYS.SYSTEM_USER}>
              <SystemUserSection
                systemUserDetails={systemUserDetails}
                title={customTitle}
                hasManagePermission={canManageSystemUsers}
                isEditing={isEditingSystemUser}
                onEditingChange={setEditingSystemUser}
                onSaved={handleSystemUserSaved}
              />
            </TabSwitch>
          )}
        </ModalPanel>
      </ModalSidebar>

      {/* IntegrationModals - handles the 4 basic actions (reset-password, block-signin, etc.) */}
      <IntegrationModals
        closeIntegrationModal={closeIntegrationModal}
        modalData={modalData}
        onSuccessUpdateIntegrationManager={() => {
          handleEmployeeMutationComplete(true)
        }}
        openedModal={openedModal}
        selectedEmployees={[employee]}
        onBackToEmployeeModal={closeIntegrationModal}
      />

      {/* Extended modals - handles IntegrationManager and other modals */}
      <EmployeeActionsModals
        action={action}
        modal={modal}
        closeModal={closeModal}
        employee={employee}
        selectedCustomer={selectedCustomer}
        allEmployeesGroups={allEmployeesGroupsFinal}
        allEmployees={allEmployeesFromHook}
        selectedIntegration={directoryIntegration}
        onUpdate={handleEmployeeMutationComplete}
      />

      <EditEmployeeDataModal
        show={showEditModal}
        onHide={handleEditModalClose}
        onUpdate={handleEditModalUpdate}
        employee={employee}
        selectedCustomer={selectedCustomer}
        allEmployeesGroups={allEmployeesGroupsFinal}
        allEmployees={allEmployeesFromHook}
        headerBackButtonAction={handleEditModalClose}
        selectedIntegration={directoryIntegration}
        selectedIntegrationType={selectedIntegrationTypeFromPage}
      />
    </MailboxDelegationsProvider>
  )
}

export default injectIntl(ViewEmployeeDataModal)
